import React, { memo } from "react";

import BuildIcon from "@mui/icons-material/Build";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Box,
  Divider,
  Stack,
  Tooltip,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";
import { Handle, Position, type NodeProps } from "@xyflow/react";

import type {
  PipelineGroupService,
  PipelineNodeData,
} from "src/domains/application/hooks/usePipelineGraph";

// ── Constants ─────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ReactElement> = {
  Build: <BuildIcon fontSize="small" />,
  RocketLaunch: <RocketLaunchIcon fontSize="small" />,
  CloudUpload: <CloudUploadIcon fontSize="small" />,
};

const STATUS_DOT_COLOR: Record<string, string> = {
  completed: "success.main",
  in_progress: "info.main",
  failed: "error.main",
  pending: "grey.400",
};

const STATUS_LABEL: Record<string, string> = {
  completed: "Done",
  in_progress: "Running",
  failed: "Failed",
  pending: "Queued",
};

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveDotColor(
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  palette: any,
  fallback: string,
): string {
  if (!key.includes(".")) return palette[key] ?? fallback;
  const [p, shade] = key.split(".");
  return palette[p]?.[shade] ?? fallback;
}

// ── Component ─────────────────────────────────────────────────────────────────

function PipelineGroupNodeInner({
  data,
}: NodeProps & { data: PipelineNodeData }) {
  const theme = useTheme();
  const {
    label,
    nodeType,
    status,
    iconName,
    services = [],
    deployModeBadge,
  } = data;

  const isFailed = status === "failed";
  const isInProgress = status === "in_progress";
  const isCompleted = status === "completed";

  const borderColor = isFailed
    ? theme.palette.error.main
    : isInProgress
      ? theme.palette.info.main
      : isCompleted
        ? theme.palette.success.main
        : theme.palette.divider;

  const iconBg = isFailed
    ? theme.palette.error.main
    : isInProgress
      ? theme.palette.info.main
      : isCompleted
        ? theme.palette.success.main
        : theme.palette.grey[300];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const palette = theme.palette as any;

  const completedCount = (services as PipelineGroupService[]).filter(
    (s) => s.status === "completed",
  ).length;
  const totalCount = (services as PipelineGroupService[]).length;

  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: borderColor, width: 8, height: 8, border: "none" }}
      />

      <Box
        data-node-type={nodeType}
        data-status={status}
        sx={{
          minWidth: 200,
          maxWidth: 260,
          border: `1.5px solid ${borderColor}`,
          borderRadius: 2,
          bgcolor: theme.palette.background.paper,
          boxShadow: 1,
          cursor: "pointer",
          transition: "border-color 0.3s, box-shadow 0.3s",
          overflow: "hidden",
          "&:hover": {
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
        }}
      >
        {/* ── Header row ── */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ px: 1.5, py: 1 }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: iconBg,
              color: "white",
              flexShrink: 0,
              ...(isInProgress && { animation: `${pulse} 1.5s infinite` }),
            }}
          >
            {ICON_MAP[iconName] ?? <BuildIcon fontSize="small" />}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {label}
              </Typography>
              {deployModeBadge && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: 0.5,
                    px: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  {deployModeBadge}
                </Typography>
              )}
            </Box>
          </Box>

          <Tooltip title={STATUS_LABEL[status] ?? status}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: resolveDotColor(
                    STATUS_DOT_COLOR[status] ?? "grey.400",
                    palette,
                    theme.palette.grey[400],
                  ),
                  ...(isInProgress && { animation: `${pulse} 1.5s infinite` }),
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.65rem",
                  color: isFailed
                    ? "error.main"
                    : isInProgress
                      ? "info.main"
                      : isCompleted
                        ? "success.main"
                        : "text.secondary",
                  fontWeight: 600,
                }}
              >
                {totalCount > 0
                  ? `${completedCount}/${totalCount}`
                  : (STATUS_LABEL[status] ?? status)}
              </Typography>
            </Stack>
          </Tooltip>
        </Stack>

        {/* ── Service rows ── */}
        {(services as PipelineGroupService[]).length > 0 && (
          <>
            <Divider sx={{ borderColor: "divider" }} />
            <Stack direction="column" spacing={0} sx={{ px: 1.5, py: 0.75 }}>
              {(services as PipelineGroupService[]).map((svc) => {
                const dotKey = STATUS_DOT_COLOR[svc.status] ?? "grey.400";
                const dotColor = resolveDotColor(
                  dotKey,
                  palette,
                  theme.palette.grey[400],
                );
                const svcInProgress = svc.status === "in_progress";

                return (
                  <Tooltip
                    key={svc.name}
                    title={`${svc.name}: ${svc.status}${svc.message ? ` — ${svc.message}` : ""}`}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        py: 0.3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: dotColor,
                          flexShrink: 0,
                          ...(svcInProgress && {
                            animation: `${pulse} 1.5s infinite`,
                          }),
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          color:
                            svc.status === "failed"
                              ? "error.main"
                              : svc.status === "completed"
                                ? "text.primary"
                                : "text.secondary",
                          fontWeight: svc.status === "in_progress" ? 600 : 400,
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {svc.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.6rem",
                          color:
                            svc.status === "failed"
                              ? "error.main"
                              : svc.status === "in_progress"
                                ? "info.main"
                                : svc.status === "completed"
                                  ? "success.main"
                                  : "text.disabled",
                          fontWeight: 500,
                          flexShrink: 0,
                        }}
                      >
                        {STATUS_LABEL[svc.status] ?? svc.status}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          </>
        )}
      </Box>

      <Handle
        type="source"
        position={Position.Right}
        style={{ background: borderColor, width: 8, height: 8, border: "none" }}
      />
    </>
  );
}

export const PipelineGroupNode = memo(
  PipelineGroupNodeInner,
) as typeof PipelineGroupNodeInner;
